// src/Home.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logUxEvent } from './telemetry/uxLogger';
import DecoDivider from './components/DecoDivider';
import PatientSupportFooter, {
  COMPANY_ADDRESS,
  COMPANY_CRM_REGISTRATION,
  COMPANY_FANTASIA,
  COMPANY_LEGAL_NAME,
} from './components/PatientSupportFooter';
import { useFadeNavigate } from './components/PageFade';
import { readPatientPhoneFullE164Digits } from './utils/patientPhoneStorage';
import { openDocExternalInApp } from './utils/openDocExternalInApp';
import PageSeo from './seo/PageSeo';
import { ensureSignupSession, primeSignupUxSession } from './utils/ensureSignupSession';
import { usesGuidedCopy } from './utils/signupUxAb';
import { homeSignupCta } from './utils/signupUxCopy';
import { isOrgHost, maybeJumpOrgSignupToApp } from './lib/siteHosts';
import {
  ORG_ADS_HOME_CTA,
  ORG_SAFE_BRAND_SUB,
  ORG_SAFE_SEO_DESCRIPTION,
  orgSafeCarouselCards,
  orgSafeReviews,
} from './utils/orgAdsSafeCopy';

/**
 * CTA da Home / hubs → abre sessão de cadastro e vai para a primeira
 * rota. O A/B de ordem está suspenso: isso é sempre `/consulta`.
 */
async function goToSignupStart(navigate) {
  if (maybeJumpOrgSignupToApp()) return;
  try {
    const session = await ensureSignupSession({ resetFields: true });
    navigate(session.firstRoute || '/consulta', { state: { signupFresh: true } });
  } catch {
    navigate('/consulta', { state: { signupFresh: true } });
  }
}

const publicUrl = process.env.PUBLIC_URL || '';
// WebP 320px (~3 KB). O PNG 1024² (437 KB) era o LCP no mobile (p75 ~5 s).
const HERO_LOGO_URL = `${publicUrl}/email-logo-hero.webp`;

const APPLE_STORE_URL = 'https://apps.apple.com/br/app/consulta-mais/id6748291915';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.consultamais.app4&hl=pt_BR';
const CFM_BUSCA_URL = 'https://portal.cfm.org.br/busca-medicos';

/** Avaliações 5 estrelas — Apple Store RSS + Google Play (jun/2026). */
const CUSTOMER_REVIEWS_5STAR = [
  {
    author: 'pedriinho63',
    title: 'Atendimento excelente',
    text:
      'Os médicos atendem de forma rápido e precisa, encaminham as receitas e indicações direitinhos e caso necessário o atestado também é disponibilizado na consulta.',
    source: 'Apple Store',
  },
  {
    author: 'Neves@33',
    title: 'Confiável',
    text:
      'O médico é atencioso e se preocupa em saber o que você realmente tem para melhor te avaliar.',
    source: 'Apple Store',
  },
  {
    author: 'Sabrine93',
    title: 'Atendimento ágil e solicito',
    text:
      'Médico super atencioso, sempre pronto para atender e responder todas as dúvidas.',
    source: 'Apple Store',
  },
  {
    author: 'Marcus Dottlinger',
    title: '',
    text:
      'Excelente! Consulta foi rápida, e demorou quase 6 horas para chegar o atestado e a receita. Estava preocupado, mas deu tudo certo! O médico é excelente, diagnóstico preciso! Tanto a receita quanto o atestado são enviados pelo chat do aplicativo e no e-mail pessoal. Recomendo demais!',
    source: 'Google Play',
  },
  {
    author: 'Lucas Voltolini',
    title: '',
    text:
      'É um bom atendimento. A triagem ocorre via assistente virtual que funciona muito bem e após a triagem o médico lhe atende diretamente. É tudo muito rápido e claro. Se você estiver num aperto e precisar de um atendimento rápido, eu recomendo.',
    source: 'Google Play',
  },
  {
    author: 'Enaiv Sotam',
    title: '',
    text:
      'Iniciei o processo desconfiada, mas como estava em necessidade, arrisquei. Fui rapidamente atendida com paciência e atenção. O médico foi muito atencioso, paciente e generoso com atestado. Super recomendo.',
    source: 'Google Play',
  },
  {
    author: 'Antunes Brito',
    title: '',
    text:
      'Nossa, foi muito rápido. Falei com o doutor e meu problema foi resolvido, tudo em menos de uma hora. Perdi minha consulta com meu médico de rotina, e estava desesperada porque não tinha mais vagas. Quando cheguei aqui tive receio, mas deu tudo certo!!!',
    source: 'Google Reviews',
  },
  {
    author: 'Heitor Abreu',
    title: '',
    text:
      'Pesquisei muito antes de fazer, e realmente dá um medo por não ter credibilidade. Mas recomendo. O CRM é válido, tudo por chat. Foi rápido e objetivo, me atendeu no que eu precisava no momento.',
    source: 'Google Reviews',
  },
  {
    author: 'Edivan Sousa',
    title: '',
    text:
      'A consulta é excelente, o app funciona muito bem, com triagem super rápida e detalhada... estou gostando.',
    source: 'Google Reviews',
  },
];

const REVIEW_SOURCE_ICON = {
  'Apple Store': '🍎',
  'Google Play': '▶',
  'Google Reviews': 'G',
};

/** Cache do logo do hero antes do mount (evita decode progressivo visível). */
function preloadHeroLogo() {
  if (typeof window === 'undefined') return;
  const img = new Image();
  img.src = HERO_LOGO_URL;
}

preloadHeroLogo();

const Home = () => {
  const navigate = useNavigate();
  const fadeNavigate = useFadeNavigate();
  const carouselRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const bannerLoggedRef = useRef(false);

  // Instrumentação do drop-off de 53,8% Home → /consulta:
  //  home_loaded         — primeira visualização (com referrer + flags visíveis)
  //  home_first_interaction — qualquer scroll/click/key na página, com delay
  //  home_left           — saída da Home (cleanup), com tempo total e CTA clicado
  // Combinado com `scroll_depth` (genérico) e `home_cta_click` (já existente).
  const homeMountAtRef = useRef(0);
  const interactedRef = useRef(false);
  const ctaClickedRef = useRef(null);
  // Controla a roleta automática: uma vez que o usuário interage com o
  // carrossel (toque, arrasto ou wheel), o avanço automático
  // é interrompido de vez, para que ele possa navegar livremente até o card
  // que quiser sem ser "empurrado" pelo timer.
  const autoRotateRef = useRef(true);
  const autoRotateIntervalRef = useRef(null);
  // Diferencia mudanças "programáticas" de currentIndex (auto-rotate ou teclas)
  // de mudanças disparadas pelo scroll manual do usuário. Só nas
  // programáticas é que fazemos scrollTo — caso contrário o scrollTo briga com
  // o gesto de arrasto e empurra o carrossel de volta para o último slide
  // que estava fixado.
  const programmaticChangeRef = useRef(false);

  // Banner mobile "baixe o app"
  const [showMobileBanner, setShowMobileBanner] = useState(false);

  // Sticky CTA mobile: barra fixa "Iniciar consulta agora" no rodapé quando
  // o CTA do hero sai da viewport, escondida quando o rodapé (com o segundo
  // CTA de logo) entra. Só ativa em telas ≤ 768px. Espelho do sticky-cta da
  // landing promo.
  const [showStickyCta, setShowStickyCta] = useState(false);
  const orgSafe = isOrgHost();
  // O sorteio precisa existir no primeiro paint — senão o CTA da Home
  // fica sempre no controle e o braço `guided` nunca mostra
  // "Checar médicos disponíveis" para visitante novo.
  if (!orgSafe) primeSignupUxSession();
  const homeCta = orgSafe ? ORG_ADS_HOME_CTA : homeSignupCta({ guided: usesGuidedCopy() });

  // Selo "Verificada pelo Reclame Aqui" no hero. O bundle lê os atributos
  // data-* do próprio <script> e injeta o selo no elemento data-target.
  useEffect(() => {
    const target = document.getElementById('ra-verified-seal');
    if (!target) return undefined;
    if (document.getElementById('ra-embed-verified-seal')) return undefined;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'ra-embed-verified-seal';
    script.src = 'https://s3.amazonaws.com/raichu-beta/ra-verified/bundle.js';
    script.async = true;
    script.setAttribute(
      'data-id',
      'cTkxVkRBSko3Smg2S3Budzplc21hbmhvdHRvLXNlcnZpY29zLW1lZGljb3MtbHRkYQ=='
    );
    script.setAttribute('data-target', 'ra-verified-seal');
    script.setAttribute('data-model', 'horizontal_3');
    target.appendChild(script);

    return undefined;
  }, []);

  // Estado auxiliar pra reduzir CLS: o card de promo aparece async após a API
  // responder. Reservamos espaço com um placeholder até a decisão chegar; assim
  // o conteúdo abaixo (DecoDivider, carrossel) não pula quando o card materializa.
  const [firstConsultPromoChecked, setFirstConsultPromoChecked] = useState(false);

  // Card de divulgação do R$ 19,90 de primeira consulta no hero. A promoção foi
  // encerrada em 2026-04-22; começa oculto e só é exibido se o backend indicar
  // explicitamente eligible=true (o que, enquanto a flag do backend estiver
  // desligada, nunca acontece). Manter a lógica reativa permite reativar no
  // futuro apenas ligando a flag no backend.
  const [showFirstConsultPromo, setShowFirstConsultPromo] = useState(false);

  // ==== DETECÇÃO DE AMBIENTE APP/PWA ====
  const isStandalonePWA =
    (typeof window !== 'undefined' &&
      (window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone === true)) || false;

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const urlParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const isInNativeApp = () => {
    try {
      // React Native WebView (Android/iOS)
      if (typeof window.ReactNativeWebView !== 'undefined') return true;

      // iOS WKWebView com messageHandlers definidos
      if (
        typeof window.webkit !== 'undefined' &&
        window.webkit?.messageHandlers &&
        Object.keys(window.webkit.messageHandlers).length > 0
      ) {
        return true;
      }

      // User-Agent customizado configurado no wrapper nativo
      if (/ConsultaMaisApp/i.test(ua)) return true;

      // Query param opcional para o wrapper abrir a URL com ?app=1
      if (urlParams.get('app') === '1') return true;

      // Flag persistida pelo wrapper
      if (localStorage.getItem('isNativeApp') === '1') return true;

      return false;
    } catch {
      return false;
    }
  };

  // Redirect para chat ativo agora e gerenciado pelo PatientLayout (App.js)
  // para evitar redirects duplicados e conflitos de timing.

  // Avanço automático do carrossel a cada 5s — mas só enquanto o usuário
  // ainda não tocou/interagiu com ele. Desativa quando o usuário prefere
  // movimento reduzido (acessibilidade).
  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      autoRotateRef.current = false;
      return undefined;
    }
    autoRotateIntervalRef.current = setInterval(() => {
      if (!autoRotateRef.current) return;
      const len = carouselRef.current?.children.length || 0;
      programmaticChangeRef.current = true;
      setCurrentIndex((prev) => (prev + 1) % (len || 1));
    }, 5000);
    return () => {
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
        autoRotateIntervalRef.current = null;
      }
    };
  }, []);

  const stopAutoRotate = () => {
    if (!autoRotateRef.current) return;
    autoRotateRef.current = false;
    if (autoRotateIntervalRef.current) {
      clearInterval(autoRotateIntervalRef.current);
      autoRotateIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (showMobileBanner && !bannerLoggedRef.current) {
      bannerLoggedRef.current = true;
      logUxEvent('home_banner_shown', { variant: 'mobile_app' });
    }
  }, [showMobileBanner]);

  // Sticky CTA mobile: observa o CTA do hero (.cta-wrapper) e o rodapé
  // (.footer-wrapper). Aparece quando o primeiro sai da viewport e o
  // segundo ainda não entrou. Toggle via classe no body para permitir que
  // o CSS ajuste padding do footer e esconda o banner "Baixar" ao mesmo
  // tempo. Só ativa em viewport mobile (≤768px); desktop nunca mostra.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    if (typeof IntersectionObserver !== 'function') return undefined;

    const mq = window.matchMedia('(max-width: 768px)');
    let heroCtaVisible = true;
    let footerVisible = false;
    let observer = null;

    const applyState = (show) => {
      setShowStickyCta(show);
      document.body.classList.toggle('show-home-sticky-cta', show);
    };

    const updateSticky = () => {
      if (!mq.matches) {
        applyState(false);
        return;
      }
      applyState(!heroCtaVisible && !footerVisible);
    };

    const attach = () => {
      const heroCta = document.querySelector('.cta-wrapper');
      const footer = document.querySelector('.footer-wrapper');
      if (!heroCta) return false;

      observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === heroCta) heroCtaVisible = entry.isIntersecting;
          if (entry.target === footer) footerVisible = entry.isIntersecting;
        }
        updateSticky();
      }, { threshold: 0 });

      observer.observe(heroCta);
      if (footer) observer.observe(footer);
      return true;
    };

    // O footer só existe após o mount completo dos filhos assíncronos (cards
    // de reviews, promo etc.). Tenta anexar imediatamente; se falhar, aguarda
    // via MutationObserver até ambos existirem.
    let mutation = null;
    if (!attach()) {
      mutation = new MutationObserver(() => {
        if (attach()) {
          mutation.disconnect();
          mutation = null;
        }
      });
      mutation.observe(document.body, { childList: true, subtree: true });
    }

    const onMqChange = () => updateSticky();
    if (mq.addEventListener) mq.addEventListener('change', onMqChange);
    else mq.addListener(onMqChange);

    return () => {
      if (observer) observer.disconnect();
      if (mutation) mutation.disconnect();
      if (mq.removeEventListener) mq.removeEventListener('change', onMqChange);
      else mq.removeListener(onMqChange);
      document.body.classList.remove('show-home-sticky-cta');
    };
  }, []);

  // home_loaded: dispara no mount; home_left: dispara no unmount (cleanup).
  // Captamos referrer e flags visíveis para correlacionar drop-off com origem
  // de tráfego (Google Ads vs orgânico vs direct) e com elementos exibidos.
  useEffect(() => {
    homeMountAtRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      logUxEvent('home_loaded', {
        has_first_consult_promo: !!showFirstConsultPromo,
        banner_visible: !!showMobileBanner,
        referrer: (typeof document !== 'undefined' && document.referrer) ? document.referrer.slice(0, 120) : 'direct',
      });
    } catch {}
    return () => {
      try {
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const timeMs = Math.round(Math.max(0, now - homeMountAtRef.current));
        logUxEvent('home_left', {
          time_on_page_ms: timeMs,
          cta_clicked: ctaClickedRef.current,
          interacted: interactedRef.current,
        });
      } catch {}
    };
    // Captura snapshot inicial das flags; mudanças posteriores não invalidam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // home_first_interaction: dispara UMA vez por sessão na primeira ação real
  // (scroll, click ou key). Mede hesitação — visitantes que saem sem nem
  // interagir provavelmente são tráfego frio (bot, share preview, click errado).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onFirst = (e) => {
      if (interactedRef.current) return;
      interactedRef.current = true;
      try {
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const delay = Math.round(Math.max(0, now - homeMountAtRef.current));
        logUxEvent('home_first_interaction', {
          interaction_type: e.type,
          delay_ms: delay,
        });
      } catch {}
      window.removeEventListener('scroll', onFirst, true);
      window.removeEventListener('click', onFirst, true);
      window.removeEventListener('keydown', onFirst, true);
      window.removeEventListener('touchstart', onFirst, true);
    };
    window.addEventListener('scroll', onFirst, { capture: true, passive: true });
    window.addEventListener('click', onFirst, true);
    window.addEventListener('keydown', onFirst, true);
    window.addEventListener('touchstart', onFirst, { capture: true, passive: true });
    return () => {
      window.removeEventListener('scroll', onFirst, true);
      window.removeEventListener('click', onFirst, true);
      window.removeEventListener('keydown', onFirst, true);
      window.removeEventListener('touchstart', onFirst, true);
    };
  }, []);

  // Consulta o backend para saber se o paciente pode usar o desconto de
  // primeira consulta. Enquanto a promoção estiver desligada no backend
  // (FIRST_CONSULT_AUTO_ENABLED=false), este endpoint sempre responde
  // eligible=false e o card permanece oculto. Se algum dia for reativado no
  // backend, o card volta a aparecer automaticamente para quem for elegível.
  useEffect(() => {
    let patientId = '';
    try {
      patientId =
        (typeof localStorage !== 'undefined' && localStorage.getItem('patientId')) ||
        (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('patientId')) ||
        '';
    } catch {}
    if (!patientId) return;
    const apiBase =
      process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'development'
        ? 'http://localhost:5000'
        : (typeof window !== 'undefined' ? window.location.origin : ''));
    let patientCpf = '';
    let patientEmail = '';
    let patientPhone = '';
    try {
      patientCpf = localStorage.getItem('patientCpf') || '';
      patientEmail = localStorage.getItem('patientEmail') || '';
      patientPhone = readPatientPhoneFullE164Digits();
    } catch {}
    const ctrl = new AbortController();
    fetch(`${apiBase}/coupons/first-consult-eligibility`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patientId,
        ...(patientCpf ? { cpf: patientCpf } : {}),
        ...(patientEmail ? { email: patientEmail } : {}),
        ...(patientPhone ? { phone: patientPhone } : {}),
      }),
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        setShowFirstConsultPromo(!!(data && data.eligible === true));
      })
      .catch(() => {})
      .finally(() => {
        setFirstConsultPromoChecked(true);
      });
    return () => ctrl.abort();
  }, []);

  // Faz o scroll suave APENAS quando a mudança veio de um gatilho
  // programático (auto-rotate ou clique num dot). Mudanças disparadas pelo
  // scroll manual do usuário (handleCarouselScroll) não devem reposicionar o
  // carrossel — caso contrário brigamos com o arrasto e o usuário fica preso
  // no último slide que o auto-rotate fixou.
  useEffect(() => {
    if (!programmaticChangeRef.current) return;
    const carousel = carouselRef.current;
    const el = carousel?.children[currentIndex];
    if (!carousel || !el) {
      programmaticChangeRef.current = false;
      return;
    }
    let finished = false;
    const release = () => {
      if (finished) return;
      finished = true;
      programmaticChangeRef.current = false;
    };
    const t = window.setTimeout(release, 550);
    const onScrollEnd = () => {
      window.clearTimeout(t);
      release();
    };
    carousel.addEventListener('scrollend', onScrollEnd);
    carousel.scrollTo({ left: el.offsetLeft, behavior: 'smooth' });
    return () => {
      window.clearTimeout(t);
      carousel.removeEventListener('scrollend', onScrollEnd);
    };
  }, [currentIndex]);

  // Marca visual de ativo: roda em qualquer mudança de currentIndex,
  // inclusive quando o usuário rola manualmente.
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    Array.from(carousel.children).forEach((c, i) =>
      c.classList.toggle('active', i === currentIndex)
    );
  }, [currentIndex]);

  // Exibe o banner somente em telas pequenas e se o usuário não tiver fechado antes,
  // e NUNCA em app nativo (WebView) ou PWA instalado.
  useEffect(() => {
    // Se veio ?app=1, já grava a flag para as próximas visitas
    if (urlParams.get('app') === '1') {
      localStorage.setItem('isNativeApp', '1');
    }

    // Listener para o wrapper nativo reforçar a flag (postMessage).
    // Onda 2D hardening 2026-06: valida origin antes de aceitar mensagem.
    // Allowlist: mesma origem do app (web), origem nula do React Native
    // WebView ('') e protocolos custom file:///about: usados em wrappers.
    const ALLOWED_MSG_ORIGINS = new Set([
      window.location.origin,
      'null',
      '',
      'https://consultamais.app',
      'https://www.consultamais.app',
    ]);
    const onMessage = (evt) => {
      try {
        if (!ALLOWED_MSG_ORIGINS.has(String(evt?.origin || ''))) {
          // Origem nao confiavel — ignora silenciosamente.
          return;
        }
        const data = evt?.data;
        if (!data) return;
        if (data === 'CONSULTAMAIS_APP') {
          localStorage.setItem('isNativeApp', '1');
          setShowMobileBanner(false);
          return;
        }
        if (typeof data === 'string') {
          const parsed = JSON.parse(data);
          if (parsed?.type === 'CONSULTAMAIS_APP') {
            localStorage.setItem('isNativeApp', '1');
            setShowMobileBanner(false);
          }
        }
      } catch {
        // silencia parsing errors
      }
    };
    window.addEventListener('message', onMessage);

    const dismissed = localStorage.getItem('hideMobileAppBanner') === '1';

    const checkMobile = () => {
      const isMobile = window.innerWidth <= 768;
      const inApp = isInNativeApp();
      const show = isMobile && !dismissed && !isStandalonePWA && !inApp;
      setShowMobileBanner(show);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // App Android (WebView): o sistema costuma ampliar fontes/UI além do CSS
  // usado na web e no iOS. Marcamos o body para overrides pontuais na Home.
  const isAndroidNativeApp = /Android/i.test(ua) && isInNativeApp();
  useEffect(() => {
    if (!isAndroidNativeApp) return undefined;
    document.body.classList.add('home-android-native');
    return () => document.body.classList.remove('home-android-native');
  }, [isAndroidNativeApp]);

  const closeMobileBanner = () => {
    localStorage.setItem('hideMobileAppBanner', '1');
    setShowMobileBanner(false);
    logUxEvent('home_banner_dismissed', { variant: 'mobile_app' });
  };

  // Sincroniza currentIndex com o scroll manual do carrossel
  const handleCarouselScroll = () => {
    if (programmaticChangeRef.current) return;
    const carousel = carouselRef.current;
    if (!carousel?.children?.length) return;
    const { scrollLeft, clientWidth } = carousel;
    const children = Array.from(carousel.children);
    const center = scrollLeft + clientWidth / 2;
    // Card mais próximo do centro, não o primeiro que ultrapassa o centro: os
    // cards têm 86% da largura e snap central, então o primeiro repousa
    // encostado à esquerda (scrollLeft 0) sem nunca centralizar — pela regra do
    // "primeiro que ultrapassa", ele era pulado e o card 2 virava o ativo.
    let closest = 0;
    let smallestGap = Infinity;
    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      const gap = Math.abs(el.offsetLeft + el.offsetWidth / 2 - center);
      if (gap < smallestGap) {
        smallestGap = gap;
        closest = i;
      }
    }
    setCurrentIndex(closest);
  };

  const REVIEWS_URL = '/reviews';

  const spaNavigate = (path) => (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    navigate(path);
    // ScrollToTop global também cobre; reset imediato evita flash no meio
    // da Home durante o fade-out do AnimatedOutlet.
    try {
      window.scrollTo(0, 0);
    } catch {
      /* ignore */
    }
  };

  const goToCarouselSlide = (index) => {
    stopAutoRotate();
    programmaticChangeRef.current = true;
    setCurrentIndex(index);
  };

  const handleCarouselKeyDown = (event) => {
    const total = carouselRef.current?.children?.length || 0;
    if (!total) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      stopAutoRotate();
      programmaticChangeRef.current = true;
      setCurrentIndex((prev) => (prev + 1) % total);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      stopAutoRotate();
      programmaticChangeRef.current = true;
      setCurrentIndex((prev) => (prev - 1 + total) % total);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      stopAutoRotate();
      programmaticChangeRef.current = true;
      setCurrentIndex(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      stopAutoRotate();
      programmaticChangeRef.current = true;
      setCurrentIndex(total - 1);
      return;
    }
  };

  const CAROUSEL_CARDS = [
    {
      title: 'A consulta é válida em qualquer lugar do Brasil?',
      lines: [
        'Sim. Os atestados e receitas emitidos têm validade em todo o território nacional.',
        'Os atestados de afastamento são aceitos por qualquer empresa ou órgão público.'
      ],
    },
    {
      title: 'Quem conhece, consulta',
      reviewsLink: true,
      lines: [
        'Mais de dez mil consultas realizadas.',
        'Nota 4,8 de 5 no Google Reviews',
      ],
    },
    {
      title: 'É possível renovar receita de uso contínuo?',
      lines: [
        'Sim. O médico pode renovar receitas de medicamentos de uso contínuo quando clinicamente indicado.',
        'A receita é emitida em PDF com assinatura digital ICP-Brasil, aceita em farmácias de todo o Brasil.',
      ],
    },
    {
      title: 'Qual é o valor da consulta?',
      lines: [
        'R$ 79,90 à vista no PIX, ou em parcelas de até 12x no cartão de crédito. Pagamento único, sem mensalidade.',
        'Cancelamento garantido: reembolso integral, solicitado diretamente pela plataforma, antes do início do atendimento. O reembolso automático fica disponível 15 minutos após a confirmação do pagamento.',
      ],
    },
    {
      title: 'As receitas e atestados digitais são documentos válidos?',
      lines: [
        'Sim. A Resolução CFM nº 2.314/2022 autoriza a emissão de documentos médicos por teleconsulta.',
        'Os documentos são válidos em todo o Brasil e assinados por médico com CRM ativo.',
        'Os atestados de afastamento são aceitos por qualquer empresa ou órgão público.'
      ],
    },
    {
      title: 'Os documentos são enviados em PDF, diretamente por e-mail?',
      lines: [
        'Sim. Atestado, receita e pedido de exame são emitidos em PDF, com assinatura digital ICP-Brasil, por médico com CRM ativo, e enviados diretamente ao seu e-mail.',
      ],
    },
    {
      title: 'Qual a diferença entre a consulta por chat e por videochamada?',
      lines: [
        'As duas modalidades são reconhecidas pela Resolução CFM 2.314/2022. No chat, paciente e médico conversam por mensagens, com envio de fotos e exames — tudo por escrito.',
        'A Consulta+ atende 100% por chat, sem videochamada, sem horário marcado.',
      ],
    },
    {
      title: 'Como validar a autenticidade de um documento emitido?',
      lines: [
        'Todos os documentos saem em PDF com assinatura digital ICP-Brasil.',
        'A autenticidade pode ser conferida em consultamais.app/autenticar ou no validador oficial do governo (ITI/Gov.br).',
      ],
    },
  ];

  const carouselCards = orgSafe ? orgSafeCarouselCards(CAROUSEL_CARDS) : CAROUSEL_CARDS;
  const customerReviews = orgSafe ? orgSafeReviews(CUSTOMER_REVIEWS_5STAR) : CUSTOMER_REVIEWS_5STAR;

  return (
    <>
      <PageSeo
        path="/"
        title="Consulta médica online por chat | Consulta+"
        description={orgSafe
          ? ORG_SAFE_SEO_DESCRIPTION
          : 'Teleconsulta médica por chat (sem videochamada) com médico real (CFM). Receita, atestado e pedido de exame em PDF, assinados digitalmente (ICP-Brasil), válidos em todo o Brasil. Renove receitas de uso contínuo sem sair de casa. Resolução CFM 2.314/2022.'}
        breadcrumbs={[{ name: 'Início', url: '/' }]}
      />
      <style>{`
        /* Poppins loaded globally in index.html */

        :root {
          --primary: #D32F2F;
          --primary-dark: #B71C1C;
          --primary-soft: rgba(211,47,47,0.12);
          --bg-page: #F9FAFB;
          --bg-surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
          --shadow-xs: 0 1px 2px rgba(0,0,0,0.04);
          --shadow-sm: 0 6px 16px rgba(0,0,0,0.06);
          --shadow-lg: 0 12px 28px rgba(0,0,0,0.08);
        }

        body {
          margin: 0;
          background:
            radial-gradient(ellipse 70% 55% at 50% 0%, rgba(211,47,47,0.08) 0%, transparent 65%),
            radial-gradient(ellipse 80% 50% at 50% 100%, rgba(211,47,47,0.05) 0%, transparent 70%),
            var(--bg-page);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
          color: var(--text-main);
          -webkit-font-smoothing: antialiased;
        }

        /* ===== HEADER / HERO ===== */
        .hero-wrapper {
          background: var(--bg-surface);
          border-bottom-left-radius: 2rem;
          border-bottom-right-radius: 2rem;
          box-shadow: none;
          border: 1px solid rgba(0,0,0,0.04);
          padding: max(2rem, env(safe-area-inset-top, 0px)) 1rem 3rem;
          max-width: 480px;
          margin: 0 auto;
          position: relative;
          overflow: hidden;
        }
        .hero-wrapper::before {
          content: "";
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 60% 80% at 50% 0%, rgba(211,47,47,0.07) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        .hero-wrapper > * {
          position: relative;
          z-index: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .info-card.active {
            transform: none;
          }
        }

        .logo-row {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        /* Auditoria UX 28/08: rage/dead click no headline e no brand-sub
           (paciente toca o texto esperando iniciar a consulta). */
        button.logo-row {
          background: transparent;
          border: 0;
          padding: 0;
          margin: 0;
          width: 100%;
          font-family: inherit;
          color: inherit;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          appearance: none;
        }
        button.logo-row:focus-visible {
          outline: 2px solid var(--brand, #B00000);
          outline-offset: 4px;
          border-radius: 12px;
        }

        .hero-mascot {
          display: flex;
          justify-content: center;
          margin: 0 0 1rem;
          width: 100%;
        }

        /* Logo como CTA: <button> resetado, mantem visual identico ao div anterior.
           Auditoria 23-24/05 mostrou rage clicks (3-7/dia) em hero-mascot — paciente
           queria clicar mas elemento nao era interativo. Agora navega para /consulta. */
        button.hero-mascot {
          background: transparent;
          border: 0;
          padding: 0;
          font-family: inherit;
          color: inherit;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          appearance: none;
        }
        button.hero-mascot:hover .hero-mascot__logo,
        button.hero-mascot:focus-visible .hero-mascot__logo {
          filter: brightness(1.04);
        }
        button.hero-mascot:active .hero-mascot__logo {
          filter: brightness(0.96);
        }
        button.hero-mascot:focus-visible {
          outline: 2px solid var(--brand, #B00000);
          outline-offset: 4px;
          border-radius: 12px;
        }

        .hero-mascot__logo {
          /* px, não rem: o WebView do Android amplia rem e ainda usa o
             width/height do <img> (320) como mínimo do item flex. */
          width: 112px;
          height: 112px;
          max-width: min(112px, 70vw);
          max-height: 112px;
          min-width: 0;
          flex: 0 0 auto;
          object-fit: contain;
          display: block;
          background: #fff;
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 1rem;
          pointer-events: none;
          transition: filter 0.15s ease;
        }

        .app-logo {
          width: 96px;
          height: auto;
          border-radius: .75rem;
          box-shadow: none;
          border: 1px solid rgba(0,0,0,0.06);
          background: #fff;
        }

        .brand-headline {
          margin: 0 0 .35rem;
          font-size: 1.65rem;
          line-height: 1.25;
          font-weight: 600;
          color: var(--text-main);
          text-align: center;
          max-width: 20rem;
        }

        .brand-sub {
          margin: 0 auto 1.5rem;
          font-size: .95rem;
          line-height: 1.5;
          font-weight: 400;
          color: var(--text-dim);
          text-align: center;
          max-width: 22rem;
        }

        /* Badge extra confiança */
        .trust-strip {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          gap: .5rem 1rem;
          font-size: .75rem;
          line-height: 1.2;
          color: var(--text-main);
          font-weight: 500;
          background: #F9FAFB;
          border-radius: var(--radius-xl);
          box-shadow: none;
          border: 1px solid rgba(0,0,0,0.04);
          padding: .75rem 1rem;
          max-width: 22rem;
          margin: 0 auto 1.5rem;
        }

        .trust-item {
          display: flex;
          align-items: center;
          gap: .4rem;
          white-space: nowrap;
        }

        .trust-icon {
          font-size: .9rem;
        }

        /* CTA principal */
        .cta-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .btn-start {
          -webkit-tap-highlight-color: transparent;
          appearance: none;
          border: 0;
          outline: 0;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
          background: var(--primary);
          color: #fff;
          /* +25% sobre baseline v0.1.41 */
          font-size: 1.3125rem;
          line-height: 1.2;
          font-weight: 600;
          border-radius: var(--radius-full);
          padding: 1.375rem 2.1875rem;
          min-width: 275px;
          min-height: 60px;
          box-shadow: none;
          transition: background .18s ease, box-shadow .18s ease;
        }
        .btn-start__icon {
          width: 1.375rem;
          height: 1.375rem;
          flex-shrink: 0;
        }
        .btn-start:hover {
          background: var(--primary-dark);
          box-shadow: var(--shadow-sm);
        }
        .btn-start:active {
          background: var(--primary-dark);
        }
        .btn-start:focus-visible {
          box-shadow: 0 0 0 4px var(--primary-soft);
        }

        .legal-hint {
          text-align: center;
          font-size: .7rem;
          line-height: 1.4;
          color: var(--text-dim);
          font-weight: 400;
          margin-top: .75rem;
          user-select: none;
        }

        /* CLS-safe slot: reserva espaço enquanto API de elegibilidade do
           promo ainda não respondeu, evitando layout shift quando o card
           materializa. */
        .first-consult-slot {
          width: 100%;
          max-width: 480px;
          margin: 1rem auto 0;
          /* Altura aproximada do card real (medida visualmente). Mantida
             enquanto !is-checked. Quando is-checked && !is-active (inelegível),
             encolhe via transition. */
          min-height: 96px;
          transition: min-height .35s ease, opacity .25s ease;
        }
        .first-consult-slot.is-checked.is-active {
          min-height: 96px;
        }
        .first-consult-slot.is-checked:not(.is-active) {
          min-height: 0;
          opacity: 0;
          margin-top: 0;
          margin-bottom: 0;
          pointer-events: none;
        }
        @media (max-width: 380px) {
          .first-consult-slot { min-height: 80px; }
          .first-consult-slot.is-checked.is-active { min-height: 80px; }
        }

        .first-consult-highlight {
          display: flex;
          align-items: center;
          gap: .9rem;
          width: calc(100% - 0px);
          max-width: 480px;
          margin: 0;
          padding: .95rem 1.05rem;
          border-radius: 16px;
          border: 0;
          background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
          color: #fff;
          text-decoration: none;
          text-align: left;
          font: inherit;
          cursor: pointer;
          box-shadow: 0 10px 28px rgba(22,163,74,0.22);
          transition: transform .12s ease, box-shadow .12s ease;
          position: relative;
          overflow: hidden;
        }
        .first-consult-highlight:hover,
        .first-consult-highlight:focus-visible {
          transform: translateY(-1px);
          box-shadow: 0 14px 34px rgba(22,163,74,0.28);
          outline: none;
        }
        .first-consult-highlight__value {
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-width: 78px;
          padding: .45rem .55rem;
          border-radius: 12px;
          background: rgba(255,255,255,0.16);
          border: 1px solid rgba(255,255,255,0.28);
          line-height: 1.1;
        }
        .first-consult-highlight__value-money {
          font-size: 1.05rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          white-space: nowrap;
        }
        .first-consult-highlight__value-label {
          font-size: .6rem;
          font-weight: 700;
          letter-spacing: .08em;
          text-transform: uppercase;
          opacity: 0.9;
          margin-top: 2px;
        }
        .first-consult-highlight__body {
          flex: 1 1 auto;
          min-width: 0;
        }
        .first-consult-highlight__pill {
          display: inline-block;
          font-size: .6rem;
          font-weight: 700;
          letter-spacing: .08em;
          text-transform: uppercase;
          background: rgba(255,255,255,0.2);
          color: #fff;
          padding: 2px 8px;
          border-radius: 999px;
          margin-bottom: .3rem;
        }
        .first-consult-highlight__title {
          font-size: .98rem;
          font-weight: 800;
          line-height: 1.25;
          letter-spacing: -0.01em;
          margin: 0;
        }
        .first-consult-highlight__sub {
          font-size: .78rem;
          line-height: 1.4;
          opacity: 0.95;
          margin: .25rem 0 0;
        }
        .first-consult-highlight__fine {
          font-size: .68rem;
          line-height: 1.35;
          opacity: 0.82;
          margin: .3rem 0 0;
          font-weight: 500;
        }
        .first-consult-highlight__arrow {
          flex: 0 0 auto;
          font-size: 1.3rem;
          font-weight: 700;
          opacity: 0.9;
          padding-left: .2rem;
        }

        @media (max-width: 380px) {
          .first-consult-highlight {
            gap: .7rem;
            padding: .8rem .85rem;
          }
          .first-consult-highlight__value {
            min-width: 68px;
          }
          .first-consult-highlight__value-money {
            font-size: .95rem;
          }
          .first-consult-highlight__title {
            font-size: .92rem;
          }
          .first-consult-highlight__sub {
            font-size: .74rem;
          }
        }

        /* ===== FAQ / CARROSSEL ===== */
        .carousel-outer {
          max-width: 480px;
          margin: 2.25rem auto 0;
          padding-bottom: 1.5rem;
        }

        .carousel-title {
          font-size: .9rem;
          font-weight: 500;
          color: var(--text-dim);
          padding: 0 1rem .5rem;
          display: flex;
          align-items: baseline;
        }

        .carousel-shell {
          position: relative;
        }

        .carousel-shell::before,
        .carousel-shell::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 2rem;
          width: 1.25rem;
          pointer-events: none;
          z-index: 1;
        }

        .carousel-shell::before {
          left: 0;
          background: linear-gradient(to right, var(--bg-page), transparent);
        }

        .carousel-shell::after {
          right: 0;
          background: linear-gradient(to left, var(--bg-page), transparent);
        }

        .carousel-main {
          display: flex;
          gap: .85rem;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          padding: 0 1rem 1rem;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .carousel-main::-webkit-scrollbar {
          display: none;
        }

        .info-card {
          flex: 0 0 86%;
          background: var(--bg-surface);
          border-radius: 1.25rem;
          padding: 1.25rem 1.35rem 1.35rem;
          scroll-snap-align: center;
          border: 1px solid rgba(0,0,0,0.05);
          box-shadow: var(--shadow-sm);
          transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease;
        }

        .info-card.active {
          border-color: rgba(211,47,47,0.2);
          box-shadow: var(--shadow-lg);
          transform: scale(1.015);
        }

        .info-headline {
          font-size: 1.05rem;
          font-weight: 600;
          line-height: 1.35;
          letter-spacing: -.015em;
          color: var(--text-main);
          margin: 0 0 .85rem;
          padding-bottom: .65rem;
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        .info-headline a {
          color: inherit;
          text-decoration: none;
        }

        .info-headline a:hover,
        .info-headline a:focus-visible {
          color: var(--primary);
        }

        .info-line {
          position: relative;
          font-size: .875rem;
          line-height: 1.5;
          font-weight: 400;
          color: var(--text-dim);
          margin: .5rem 0 0;
          padding-left: .85rem;
        }

        .info-line::before {
          content: "";
          position: absolute;
          left: 0;
          top: .58em;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: var(--primary);
          opacity: .4;
        }

        .info-line.linklike {
          color: var(--primary);
          font-weight: 600;
        }

        .info-line.linklike::before {
          opacity: 1;
        }

        .info-line a {
          color: inherit;
          text-decoration: none;
        }

        .info-line a:hover,
        .info-line a:focus-visible {
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .carousel-dots {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: .4rem;
          padding: .15rem 1rem 0;
        }

        .carousel-dot {
          width: 6px;
          height: 6px;
          padding: 0;
          border: none;
          border-radius: 999px;
          background: rgba(30,30,30,0.14);
          cursor: pointer;
          transition: width .2s ease, background .2s ease;
        }

        .carousel-dot.active {
          width: 1.35rem;
          background: var(--primary);
        }

        .carousel-dot:focus-visible {
          outline: 2px solid var(--primary-soft);
          outline-offset: 2px;
        }

        /* ===== CONFIANÇA (abaixo do FAQ) ===== */
        .home-trust-block {
          max-width: 480px;
          margin: 1.75rem auto 0;
          padding: 0 1rem 0.25rem;
        }

        .home-trust-doctor__text {
          margin: 0 0 0.55rem;
          font-size: 0.82rem;
          line-height: 1.55;
          color: var(--text-dim);
        }

        .home-trust-doctor__text:last-child {
          margin-bottom: 0;
        }

        .home-trust-block__title {
          font-size: 1.05rem;
          font-weight: 600;
          color: var(--text-main);
          letter-spacing: 0.005em;
          margin: 0 0 0.35rem;
        }

        .home-trust-block__sub {
          font-size: 0.82rem;
          color: var(--text-dim);
          line-height: 1.45;
          margin: 0 0 1rem;
        }

        .home-trust-block__eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--primary);
          margin: 0 0 0.55rem;
        }

        .home-trust-block__headline {
          font-size: 0.98rem;
          font-weight: 700;
          line-height: 1.35;
          color: var(--text-main);
          margin: 0 0 0.65rem;
        }

        .home-trust-block__headline--center {
          text-align: center;
        }

        .home-trust-subsection--center {
          text-align: center;
          margin-top: 0.25rem;
        }

        .home-trust-subsection--center .home-trust-body-text {
          text-align: center;
        }

        .home-trust-list--center {
          list-style: none;
          padding: 0;
          margin: 0 0 0.75rem;
          text-align: center;
        }

        .home-trust-block--center {
          text-align: center;
        }

        .home-trust-block--center .home-trust-block__title,
        .home-trust-block--center .home-trust-block__sub {
          text-align: center;
        }

        .home-trust-block--center .home-trust-block__eyebrow {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .home-trust-block--center .home-trust-doctor {
          justify-content: center;
        }

        .home-trust-block--center .home-trust-body-text {
          text-align: center;
        }

        .home-trust-reviews-kicker {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          gap: 0.35rem;
          margin: 0.45rem 0 1.15rem;
          padding: 0.6rem 1.25rem;
          border-radius: 999px;
          background: linear-gradient(135deg, rgba(211, 47, 47, 0.07) 0%, rgba(255, 248, 225, 0.92) 100%);
          border: 1px solid rgba(211, 47, 47, 0.14);
          box-shadow: 0 4px 14px rgba(211, 47, 47, 0.06);
        }

        .home-trust-reviews-kicker__stars {
          color: #f59e0b;
          font-size: 0.95rem;
          letter-spacing: 0.16em;
          line-height: 1;
        }

        .home-trust-reviews-kicker__word {
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--primary);
        }

        .home-trust-intro--center {
          text-align: center;
          margin-bottom: 1rem;
        }

        .home-trust-intro--center .home-trust-block__eyebrow {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .home-trust-intro--center .home-trust-block__title,
        .home-trust-intro--center .home-trust-block__sub {
          text-align: center;
        }

        .home-trust-intro--center .home-trust-highlight {
          margin-left: auto;
          margin-right: auto;
        }

        .home-trust-highlight {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          background: #fff8e1;
          color: #92400e;
          font-size: 0.8rem;
          font-weight: 600;
          border-radius: 999px;
          padding: 0.28rem 0.7rem;
          margin: 0 0 0.75rem;
          border: 1px solid rgba(146, 64, 14, 0.15);
        }

        .home-trust-stores {
          display: grid;
          gap: 0.55rem;
          margin: 0 0 1rem;
        }

        .home-trust-store {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.8rem 0.85rem;
          border: 1px solid rgba(0, 0, 0, 0.07);
          border-radius: 0.85rem;
          background: var(--bg-surface);
          text-decoration: none;
          color: inherit;
          box-shadow: var(--shadow-xs);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .home-trust-store:hover,
        .home-trust-store:focus-visible {
          border-color: rgba(211, 47, 47, 0.35);
          box-shadow: var(--shadow-sm);
          outline: none;
        }

        .home-trust-store__icon {
          flex-shrink: 0;
          width: 2.25rem;
          height: 2.25rem;
          border-radius: 0.6rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          background: #fafafa;
          border: 1px solid rgba(0, 0, 0, 0.06);
        }

        .home-trust-store__body {
          flex: 1;
          min-width: 0;
        }

        .home-trust-store__title {
          margin: 0;
          font-size: 0.92rem;
          font-weight: 700;
          color: #1f2937;
        }

        .home-trust-store__sub {
          margin: 0.1rem 0 0;
          font-size: 0.78rem;
          color: #6b7280;
          line-height: 1.35;
        }

        .home-trust-store__arrow {
          flex-shrink: 0;
          color: var(--primary);
          font-weight: 700;
        }

        .home-trust-store--static {
          cursor: default;
        }

        .home-trust-store--static:hover,
        .home-trust-store--static:focus-visible {
          border-color: rgba(0, 0, 0, 0.07);
          box-shadow: var(--shadow-xs);
        }

        .home-trust-body-text {
          margin: 0 0 0.65rem;
          font-size: 0.82rem;
          line-height: 1.55;
          color: var(--text-dim);
        }

        .home-trust-body-text:last-child {
          margin-bottom: 0;
        }

        .home-trust-list {
          margin: 0 0 0.75rem;
          padding: 0 0 0 1rem;
          font-size: 0.82rem;
          line-height: 1.55;
          color: var(--text-dim);
        }

        .home-trust-list li + li {
          margin-top: 0.35rem;
        }

        .home-trust-list a {
          color: inherit;
          text-decoration: none;
        }

        .home-trust-list a:hover,
        .home-trust-list a:focus-visible {
          text-decoration: underline;
          outline: none;
        }

        .home-trust-link {
          display: inline-block;
          margin-top: 0.5rem;
          font-size: 0.82rem;
          font-weight: 600;
          color: var(--primary);
          text-decoration: none;
        }

        .home-trust-link:hover,
        .home-trust-link:focus-visible {
          text-decoration: underline;
          outline: none;
        }

        .home-example-docs {
          max-width: 480px;
          margin: 1.1rem auto 0;
          padding: 0 1.5rem;
          text-align: center;
          font-size: 0.88rem;
          line-height: 1.55;
        }

        .home-example-docs a {
          font-weight: 600;
          color: var(--primary);
          text-decoration: none;
        }

        .home-example-docs a:hover,
        .home-example-docs a:focus-visible {
          text-decoration: underline;
          outline: none;
        }

        .home-trust-doctor {
          display: flex;
          justify-content: center;
          margin: 0 0 0.85rem;
        }

        .home-trust-doctor__cred {
          margin: 0;
          font-size: 0.78rem;
          color: var(--text-main);
          line-height: 1.45;
        }

        .home-trust-doctor__cred a {
          color: inherit;
          text-decoration: none;
        }

        .home-trust-doctor__cred a:hover,
        .home-trust-doctor__cred a:focus-visible {
          text-decoration: underline;
          outline: none;
        }

        .home-trust-shots {
          display: grid;
          gap: 0.85rem;
        }

        .home-review-shot {
          background: linear-gradient(180deg, #fafafa 0%, #fff 100%);
          border-radius: 1rem;
          padding: 0.9rem 1rem 1.05rem;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
        }

        .home-review-shot__chrome {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          margin-bottom: 0.7rem;
          padding-bottom: 0.6rem;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        }

        .home-review-shot__store {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.72rem;
          font-weight: 700;
          color: #374151;
          letter-spacing: 0.02em;
        }

        .home-review-shot__badge {
          font-size: 0.65rem;
          font-weight: 600;
          color: #6b7280;
          background: #f3f4f6;
          border-radius: 999px;
          padding: 0.15rem 0.5rem;
        }

        .home-review-shot__stars {
          color: #f59e0b;
          font-size: 0.88rem;
          letter-spacing: 0.06em;
          margin: 0 0 0.35rem;
        }

        .home-review-shot__title {
          margin: 0 0 0.35rem;
          font-size: 0.92rem;
          font-weight: 700;
          color: var(--text-main);
          line-height: 1.35;
        }

        .home-review-shot__text {
          margin: 0 0 0.55rem;
          font-size: 0.82rem;
          line-height: 1.5;
          color: var(--text-dim);
        }

        .home-review-shot__author {
          margin: 0;
          font-size: 0.72rem;
          color: #9ca3af;
          font-weight: 500;
        }

        /* ===== RODAPÉ ===== */
        .footer-wrapper {
          max-width: 480px;
          margin: 1.5rem auto 2rem;
          padding: 0 1rem calc(2rem + env(safe-area-inset-bottom, 0px));
          color: var(--text-dim);
          text-align: center;
          font-size: .8rem;
          line-height: 1.4;
          font-weight: 400;
        }

        .footer-topline {
          margin-bottom: 1rem;
          color: var(--text-main);
          font-weight: 500;
        }

        .footer-contact {
          font-size: .8rem;
          line-height: 1.4;
          color: var(--text-main);
          font-weight: 400;
        }

        .footer-contact a {
          color: var(--primary);
          font-weight: 600;
          text-decoration: none;
        }

        /* Card de reputação ÓTIMO no Reclame Aqui (captura estática) */
        .home-ra-reputation {
          display: flex;
          justify-content: center;
          margin: 1rem 0 0;
        }
        .home-ra-reputation img {
          width: min(340px, 92vw);
          height: auto;
          border-radius: 12px;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
          transition: box-shadow .15s ease, transform .15s ease;
        }

        /* Wrapper clicável das capturas RA no hero — navega para /reviews */
        .hero-ra-link {
          display: block;
          text-decoration: none;
          color: inherit;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .hero-ra-link:hover .home-ra-reputation img,
        .hero-ra-link:focus-visible .home-ra-reputation img {
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
          transform: translateY(-1px);
        }
        .hero-ra-link:focus-visible {
          outline: 2px solid var(--primary);
          outline-offset: 4px;
          border-radius: 12px;
        }

        /* Selo "Verificada pelo Reclame Aqui" — só a imagem, sem clique/redirect */
        .footer-ra-seal {
          display: flex;
          justify-content: center;
          margin: 1rem 0 0;
        }
        .footer-ra-seal,
        .footer-ra-seal * {
          pointer-events: none;
          cursor: default;
        }

        /* Link textual abaixo dos selos RA → página oficial no Reclame Aqui */
        .hero-ra-verify-link {
          display: block;
          text-align: center;
          margin: 0.65rem auto 0;
          padding: 0 1rem;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--primary);
          text-decoration: underline;
          text-underline-offset: 2px;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .hero-ra-verify-link:hover,
        .hero-ra-verify-link:focus-visible {
          color: var(--primary-dark);
        }
        .hero-ra-verify-link:focus-visible {
          outline: 2px solid var(--primary);
          outline-offset: 3px;
          border-radius: 4px;
        }

        /* estilos adicionados para o bloco de informações do rodapé */
        .footer-info {
          margin-top: .5rem;
          font-size: .8rem;
          line-height: 1.4;
          color: var(--text-main);
          font-weight: 400;
        }

        /* link na área "somos confiáveis" com mesmo estilo do 'Fale conosco' */
        .footer-info a {
          color: var(--primary);
          font-weight: 600;
          text-decoration: none;
        }

        /* Selos de programa (Amigo, Portal, Iniciativa Marli) — original + 14% */
        .footer-program-cards {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.63rem;
          margin-top: 0.9rem;
        }
        .footer-social-seal {
          display: inline-flex;
          align-items: center;
          gap: 0.68rem;
          margin-top: 0;
          padding: 0.63rem 1.14rem;
          max-width: 100%;
          box-sizing: border-box;
          background: #F7F7F8;
          border: 1px solid rgba(30, 30, 30, 0.12);
          border-radius: 11px;
          text-decoration: none;
          text-align: left;
          transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
        }
        .footer-social-seal:hover,
        .footer-social-seal:focus-visible {
          border-color: rgba(211, 47, 47, 0.35);
          box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
          transform: translateY(-1px);
        }
        .footer-social-seal__icon {
          flex: none;
          width: 30px;
          height: 30px;
          color: var(--primary);
        }
        .footer-social-seal__label {
          font-size: 0.82rem;
          line-height: 1.3;
          color: var(--text-main);
          font-weight: 400;
        }
        .footer-social-seal__label strong {
          display: block;
          font-size: 0.89rem;
          font-weight: 700;
          color: var(--primary);
        }
        /* Selo do Programa Amigo (abaixo do hero): selo padrão + 1,25%. Fica em
           modificador próprio porque a classe base também veste os selos do
           Portal de saúde e do Divulga Saúde, no rodapé. */
        .footer-social-seal--amigo {
          gap: 0.689rem;
          padding: 0.638rem 1.154rem;
          border-radius: 11.14px;
        }
        .footer-social-seal--amigo .footer-social-seal__icon {
          width: 30.38px;
          height: 30.38px;
        }
        .footer-social-seal--amigo .footer-social-seal__label {
          font-size: 0.83rem;
        }
        .footer-social-seal--amigo .footer-social-seal__label strong {
          font-size: 0.901rem;
        }
        /* Destaque dourado — Divulga Saúde: original + 7% (demais selos +14%) */
        .footer-social-seal--gold {
          border-color: rgba(168, 133, 61, 0.55);
          gap: 0.64rem;
          padding: 0.59rem 1.07rem;
          border-radius: 11px;
        }
        .footer-social-seal--gold .footer-social-seal__icon {
          color: #A8853D;
          width: 28px;
          height: 28px;
        }
        .footer-social-seal--gold .footer-social-seal__label {
          font-size: 0.77rem;
        }
        .footer-social-seal--gold .footer-social-seal__label strong {
          color: #A8853D;
          font-size: 0.83rem;
        }
        .footer-social-seal--gold:hover,
        .footer-social-seal--gold:focus-visible {
          border-color: rgba(139, 105, 20, 0.75);
        }
        .footer-social-seal--gold:hover .footer-social-seal__label strong,
        .footer-social-seal--gold:focus-visible .footer-social-seal__label strong {
          color: #8B6914;
        }

        .footer-legal {
          max-width: 560px;
          margin: 0 auto;
          padding: 0 1rem;
          text-align: center;
        }

        .footer-legal__headline {
          margin: 0 0 0.75rem;
          font-size: 0.95rem;
          font-weight: 700;
          line-height: 1.35;
          color: var(--text-main);
        }

        .footer-legal__list {
          margin: 0 0 1.25rem;
          padding: 0;
          list-style: none;
          font-size: 0.8rem;
          line-height: 1.5;
          color: var(--text-main);
        }

        .footer-legal__list li + li {
          margin-top: 0.35rem;
        }

        .footer-legal__list a {
          color: var(--primary);
          font-weight: 600;
          text-decoration: none;
        }

        .footer-legal__list a:hover,
        .footer-legal__list a:focus-visible {
          text-decoration: underline;
        }

        .footer-legal__body {
          margin: 0 0 0.65rem;
          font-size: 0.8rem;
          line-height: 1.5;
          color: var(--text-main);
        }

        .footer-legal__body:last-child {
          margin-bottom: 0;
        }

        .footer-legal__divider {
          max-width: 480px;
          margin: 1.25rem auto 0;
          padding: 0 1.5rem;
        }

        .footer-brand {
          display: flex;
          justify-content: center;
          margin: 0 0 1.25rem;
        }

        /* Logo do rodape como CTA: mesmo padrao do mascote.
           Reseta estilos de <button> para nao mudar layout. */
        button.footer-brand {
          background: transparent;
          border: 0;
          padding: 0;
          font-family: inherit;
          color: inherit;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          appearance: none;
          width: 100%;
        }
        button.footer-brand:hover .footer-brand__logo,
        button.footer-brand:focus-visible .footer-brand__logo {
          opacity: 1;
          transform: scale(1.02);
        }
        button.footer-brand:active .footer-brand__logo {
          transform: scale(0.98);
        }
        button.footer-brand:focus-visible {
          outline: 2px solid var(--brand, #B00000);
          outline-offset: 4px;
          border-radius: 12px;
        }

        .footer-brand__logo {
          width: 104px;
          height: 104px;
          max-width: min(104px, 70vw);
          max-height: 104px;
          min-width: 0;
          flex: 0 0 auto;
          object-fit: contain;
          opacity: 0.92;
          transition: opacity 0.15s ease, transform 0.15s ease;
        }

        .trust-strip a:hover,
        .trust-strip a:focus-visible,
        .footer-contact a:hover,
        .footer-contact a:focus-visible,
        .footer-info a:hover,
        .footer-info a:focus-visible,
        .info-headline a:hover,
        .info-headline a:focus-visible,
        .info-line a:hover,
        .info-line a:focus-visible {
          text-decoration: underline;
        }

        /* ===== HINT DISCRETO: BAIXAR APP (mobile web) ===== */
        .mobile-app-banner {
          position: fixed;
          left: 50%;
          transform: translateX(-50%);
          bottom: max(.85rem, env(safe-area-inset-bottom, 0px));
          z-index: 60;
          display: none;
          align-items: center;
          gap: .55rem;
          background: rgba(255,255,255,0.96);
          backdrop-filter: saturate(160%) blur(8px);
          -webkit-backdrop-filter: saturate(160%) blur(8px);
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 999px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.06);
          padding: .5rem .65rem .5rem .9rem;
          font-size: .8rem;
          color: var(--text-dim);
          max-width: calc(100% - 2rem);
        }

        .mobile-app-banner__text {
          line-height: 1.3;
          font-weight: 400;
          white-space: nowrap;
        }

        .mobile-app-banner__btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: .25rem .65rem;
          border-radius: 999px;
          background: transparent;
          color: var(--primary);
          font-weight: 600;
          font-size: .8rem;
          text-decoration: none;
          border: none;
          cursor: pointer;
        }
        .mobile-app-banner__btn:hover { color: var(--primary-dark); }
        .mobile-app-banner__btn:focus-visible {
          outline: 2px solid var(--primary-soft);
          outline-offset: 1px;
        }

        .mobile-app-banner__close {
          appearance: none;
          background: transparent;
          border: none;
          outline: none;
          cursor: pointer;
          font-size: 1rem;
          line-height: 1;
          color: var(--text-dim);
          padding: .25rem .35rem;
          border-radius: 999px;
          opacity: .65;
          transition: opacity .18s ease;
        }
        .mobile-app-banner__close:hover { opacity: 1; }

        @media (max-width: 768px) {
          .mobile-app-banner {
            display: inline-flex;
          }
        }

        /* ===== STICKY CTA (mobile) =====
           Barra fixa no rodapé com "Iniciar consulta agora" quando o CTA do
           hero sai da viewport e antes do rodapé entrar. Espelho do padrão
           usado na landing promo (org-landing/index.html .sticky-cta).
           Heatmap Hotjar (20/07/2026) mostrou que 50% dos visitantes mobile
           abandonam antes de 20% da página; sem o sticky, o único CTA fica
           só no hero + rodapé (~8k px de distância). */
        .home-sticky-cta {
          display: none;
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 70;
          padding: .6rem .9rem calc(.6rem + env(safe-area-inset-bottom, 0px));
          background: rgba(255, 255, 255, 0.96);
          backdrop-filter: saturate(160%) blur(8px);
          -webkit-backdrop-filter: saturate(160%) blur(8px);
          border-top: 1px solid rgba(0, 0, 0, 0.07);
          box-shadow: 0 -6px 18px rgba(15, 23, 42, 0.08);
        }
        .home-sticky-cta .btn-start {
          width: 100%;
          min-width: 0;
          min-height: 52px;
          padding: .85rem 1.2rem;
          font-size: 1rem;
        }
        @media (max-width: 768px) {
          body.show-home-sticky-cta .home-sticky-cta { display: block; }
          /* Evita competição de dois elementos no rodapé: quando o sticky
             está visível, o banner "Baixar app" some. Volta assim que o
             sticky desaparece (hero ou footer visíveis). */
          body.show-home-sticky-cta .mobile-app-banner { display: none !important; }
          /* Empurra o rodapé para que o botão fixo não cubra os créditos
             legais quando o usuário chega perto do fim da página. */
          body.show-home-sticky-cta footer { padding-bottom: 6rem; }
        }

        /* ===== RESPONSIVO ===== */
        @media (min-width: 480px) {
          .hero-wrapper {
            padding: max(2.5rem, env(safe-area-inset-top, 0px)) 2rem 3rem;
            border-bottom-left-radius: 2.5rem;
            border-bottom-right-radius: 2.5rem;
          }

          .app-logo {
            width: 110px;
          }

          .brand-headline {
            font-size: 1.6rem;
          }

          .brand-sub {
            font-size: 1rem;
            max-width: 24rem;
          }

          .btn-start {
            font-size: 1.3125rem;
            min-width: 300px;
          }

          .info-card {
            flex-basis: 75%;
          }
        }

        @media (min-width: 768px) {
          .brand-headline {
            font-size: 1.8rem;
          }
          .brand-sub {
            font-size: 1rem;
          }
          .info-card {
            flex-basis: 60%;
          }
        }

        /* App Android nativo: compensa escala extra do WebView (web/iOS inalterados) */
        body.home-android-native .btn-start {
          font-size: 1.18rem;
          padding: 1.25rem 2rem;
          min-height: 54px;
          min-width: min(260px, 92vw);
        }
        @media (min-width: 480px) {
          body.home-android-native .btn-start {
            font-size: 1.18rem;
            min-width: 260px;
            padding: 1.25rem 2rem;
          }
        }
        body.home-android-native .hero-mascot__logo,
        body.home-android-native .footer-brand__logo {
          width: 96px;
          height: 96px;
          max-width: min(96px, 64vw);
          max-height: 96px;
          min-width: 0;
        }
      `}</style>

      {/* HERO / HEADER */}
      <div className="hero-wrapper">
        <button
          type="button"
          className="hero-mascot"
          onClick={() => {
            ctaClickedRef.current = 'consulta';
            logUxEvent('home_cta_click', { target: 'consulta', source: 'logo' });
            goToSignupStart(navigate);
          }}
          aria-label={homeCta}
        >
          <img
            className="hero-mascot__logo"
            src={HERO_LOGO_URL}
            alt=""
            width={112}
            height={112}
            fetchPriority="high"
            decoding="async"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          className="logo-row"
          onClick={() => {
            ctaClickedRef.current = 'consulta';
            logUxEvent('home_cta_click', { target: 'consulta', source: 'headline' });
            goToSignupStart(navigate);
          }}
          aria-label={homeCta}
        >
          <h1 className="brand-headline deco-headline">
            Atendimento médico por chat, sem sair de casa.
          </h1>
          <p className="brand-sub">
            {orgSafe
              ? ORG_SAFE_BRAND_SUB
              : 'Fale diretamente com um médico e receba, quando indicado, atestado, receita ou pedido de exame — em PDF, com validade em todo o Brasil.'}
          </p>
        </button>

        <div className="trust-strip">
          <div className="trust-item">
            <span className="trust-icon" role="img" aria-hidden="true">⭐</span>
            {/* LINK ADICIONADO NA NOTA 4,8 / 5 */}
            <a
              href="/reviews"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                navigate('/reviews');
              }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              Nota 4,8 de 5 no Google Reviews
            </a>
          </div>
          <div className="trust-item">
            <span className="trust-icon" role="img" aria-hidden="true">👥</span>
            <a
              href="/reviews"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                navigate('/reviews');
              }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              10.000+ consultas realizadas
            </a>
          </div>
        </div>

        <div className="cta-wrapper">
          <button
            type="button"
            className="btn-start"
            onClick={() => {
              ctaClickedRef.current = 'consulta';
              logUxEvent('home_cta_click', { target: 'consulta', source: 'primary_button' });
              goToSignupStart(navigate);
            }}
          >
            {homeCta}
          </button>
          <p className="legal-hint">
            Atendimento 100% online, conduzido por{' '}
            <a
              href="/diretor"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                navigate('/diretor');
              }}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              médico com CRM ativo, verificável no portal do CFM
            </a>
          </p>
          {/*
            Slot reservado para evitar CLS: enquanto a API de elegibilidade
            ainda não respondeu, mantemos um placeholder com altura aproximada
            do card. Quando a resposta chega, o card aparece (mesma altura) ou
            o slot encolhe via transition (de fora da janela de CLS).
          */}
          <div
            className={`first-consult-slot${
              firstConsultPromoChecked ? ' is-checked' : ''
            }${showFirstConsultPromo ? ' is-active' : ''}`}
            aria-hidden={!showFirstConsultPromo}
          >
            {showFirstConsultPromo && (
              <button
                type="button"
                className="first-consult-highlight"
                onClick={() => {
                  ctaClickedRef.current = 'first_consult';
                  logUxEvent('home_first_consult_click', { slot: 'highlight_card' });
                  goToSignupStart(navigate);
                }}
                aria-label="Sua primeira consulta conosco por R$ 19,90 — iniciar consulta"
              >
                <div className="first-consult-highlight__value" aria-hidden="true">
                  <span className="first-consult-highlight__value-money">R$ 19,90</span>
                  <span className="first-consult-highlight__value-label">1ª consulta</span>
                </div>
                <div className="first-consult-highlight__body">
                  <span className="first-consult-highlight__pill">Oferta automática</span>
                  <p className="first-consult-highlight__title">Sua primeira consulta conosco por R$ 19,90</p>
                  <p className="first-consult-highlight__sub">
                    Desconto aplicado sozinho no pagamento. Sem código, sem cadastro prévio.
                  </p>
                </div>
                <span className="first-consult-highlight__arrow" aria-hidden="true">›</span>
              </button>
            )}
          </div>
        </div>

        {/* Validação Reclame Aqui visível já no topo, junto da prova social.
            Clicável: leva à página completa de avaliações (/reviews). */}
        <a
          className="hero-ra-link"
          href="/reviews"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault();
            logUxEvent('home_ra_reputation_click', { target: 'reviews' });
            navigate('/reviews');
          }}
          aria-label="Reputação ÓTIMO no Reclame Aqui — ver página completa de avaliações"
        >
          <div className="home-ra-reputation">
            <img
              src={`${publicUrl}/reputacao-reclame-aqui.png`}
              alt="Reputação ÓTIMO no Reclame Aqui — nota média de 9,3 de 10 nos últimos 6 meses"
              width={556}
              height={400}
              decoding="async"
            />
          </div>

          <div
            id="ra-verified-seal"
            className="footer-ra-seal"
            aria-label="Selo Verificada pelo Reclame Aqui"
          />
        </a>
        <a
          className="hero-ra-verify-link"
          href="https://www.reclameaqui.com.br/empresa/esmanhotto-servicos-medicos-ltda/sobre/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            logUxEvent('home_ra_verify_link_click', { target: 'reclame_aqui' });
          }}
        >
          Verifique nossa reputação Reclame Aqui
        </a>

      </div>

      {/* Programa Amigo — fora do card do hero, logo abaixo */}
      <div className="footer-program-cards" style={{ maxWidth: 547, margin: '0.9rem auto 0', padding: '0 1rem' }}>
        <a
          className="footer-social-seal footer-social-seal--amigo"
          href="/amigo"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault();
            logUxEvent('home_amigo_seal_click', { target: 'amigo' });
            fadeNavigate('/amigo');
          }}
          aria-label="Conheça o Programa Amigo — ganhe R$ 40 por indicação"
        >
          <svg
            className="footer-social-seal__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="3.2" />
            <path d="M22 21v-2a3.5 3.5 0 0 0-2.5-3.35" />
            <path d="M16.5 4.15a3.2 3.2 0 0 1 0 5.7" />
          </svg>
          <span className="footer-social-seal__label">
            <strong>Programa Amigo</strong>
            Ganhe R$ 40 por indicação
          </span>
        </a>
      </div>

      {/* Arte inter-seções: teleconsulta em casa */}
      <div style={{ maxWidth: 720, margin: '2rem auto 0', padding: '0 1.5rem' }}>
        <img
          src="/img/heros/sec-home-teleconsulta-16x9.webp"
          alt="Pintura modernista de uma pessoa em casa, com um gato aos pés, lendo no celular a mensagem do médico em um balão de fala"
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: '16 / 8',
            objectFit: 'cover',
            borderRadius: '1.25rem',
            boxShadow: '0 6px 18px rgba(15,23,42,0.10)',
            display: 'block',
          }}
        />
      </div>

      <div style={{ maxWidth: 480, margin: '1.25rem auto 0', padding: '0 1.5rem' }}>
        <DecoDivider variant="chevron" />
      </div>

      {/* FAQ */}
      <section className="carousel-outer" aria-labelledby="faq-heading">
        <div className="carousel-title">
          <span
            id="faq-heading"
            className="deco-headline"
            style={{
              fontSize: '1.05rem',
              fontWeight: 600,
              color: 'var(--text-main)',
              letterSpacing: '0.005em',
            }}
          >
            Perguntas frequentes
          </span>
        </div>

        <div className="carousel-shell">
        <div
          className="carousel-main"
          ref={carouselRef}
          onScroll={handleCarouselScroll}
          onPointerDown={stopAutoRotate}
          onTouchStart={stopAutoRotate}
          onWheel={stopAutoRotate}
          onKeyDown={handleCarouselKeyDown}
          role="region"
          aria-label="Perguntas frequentes (use as setas esquerda e direita para navegar)"
          aria-roledescription="carrossel"
          tabIndex={0}
        >
          {carouselCards.map((card, i) => (
            <div
              className="info-card"
              key={i}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} de ${carouselCards.length}`}
              aria-hidden={currentIndex !== i}
            >
              <h3 className="info-headline">
                {card.reviewsLink ? (
                  <a href={REVIEWS_URL}>
                    {card.title}
                  </a>
                ) : (
                  card.title
                )}
              </h3>
              {card.lines.map((line, j) => (
                <p
                  key={j}
                  className={
                    line.includes('Google Reviews')
                      ? 'info-line linklike'
                      : 'info-line'
                  }
                >
                  {card.reviewsLink && line.includes('Nota 4,8') ? (
                    <a href={REVIEWS_URL}>
                      {line}
                    </a>
                  ) : (
                    line
                  )}
                </p>
              ))}
            </div>
          ))}
        </div>

        <div className="carousel-dots" role="tablist" aria-label="Navegação do FAQ">
          {carouselCards.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              className={`carousel-dot${currentIndex === i ? ' active' : ''}`}
              aria-label={`Pergunta ${i + 1} de ${carouselCards.length}`}
              aria-selected={currentIndex === i}
              onClick={() => goToCarouselSlide(i)}
            />
          ))}
        </div>
        </div>

        <span
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {`Slide ${currentIndex + 1} de ${carouselCards.length}: ${carouselCards[currentIndex]?.title || ''}`}
        </span>
      </section>

      {/* Arte inter-seções: documentos médicos entregues */}
      <div style={{ maxWidth: 720, margin: '2rem auto 0', padding: '0 1.5rem' }}>
        <img
          src="/img/heros/sec-home-documentos-16x9.webp"
          alt="Pintura modernista de um médico entregando um documento com selo dourado e marca de confirmação a uma paciente"
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: '16 / 8',
            objectFit: 'cover',
            borderRadius: '1.25rem',
            boxShadow: '0 6px 18px rgba(15,23,42,0.10)',
            display: 'block',
          }}
        />
      </div>

      <p className="home-example-docs">
        <a
          href="/exemplo-atestado.pdf"
          target="_blank"
          rel="noopener noreferrer"
          onClick={openDocExternalInApp}
        >
          Confira um exemplo de atestado aqui
        </a>
        {orgSafe ? null : (
          <>
            {' · '}
            <a
              href="/exemplo-receita.pdf"
              target="_blank"
              rel="noopener noreferrer"
              onClick={openDocExternalInApp}
            >
              Confira um exemplo de receita aqui
            </a>
          </>
        )}
      </p>

      <div style={{ maxWidth: 480, margin: '1.75rem auto 0', padding: '0 1.5rem' }}>
        <DecoDivider variant="chevron" />
      </div>

      {/* /reviews — avaliações e confiança */}
      <section className="home-trust-block" aria-labelledby="home-reviews-heading">
        <div className="home-trust-intro--center">
          <p className="home-trust-block__eyebrow">Avaliações · Confiança</p>
          <h2 id="home-reviews-heading" className="home-trust-block__title deco-headline">
            Avaliações de nossos pacientes
          </h2>
          <p className="home-trust-block__sub">
            Mais de 10 mil consultas realizadas · Nota 4,8 de 5 no Google Reviews
          </p>

          <span className="home-trust-highlight" role="status">
            ★ Nota 4,8 de 5 no Google Reviews
          </span>
        </div>

        <div className="home-trust-stores">
          <a
            className="home-trust-store"
            href={APPLE_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="home-trust-store__icon" aria-hidden="true">🍎</span>
            <span className="home-trust-store__body">
              <p className="home-trust-store__title">Apple Store</p>
              <p className="home-trust-store__sub">Nota 4,7 de 5 · Leia aqui todas as avaliações</p>
            </span>
            <span className="home-trust-store__arrow" aria-hidden="true">→</span>
          </a>
          <a
            className="home-trust-store"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="home-trust-store__icon" aria-hidden="true">▶</span>
            <span className="home-trust-store__body">
              <p className="home-trust-store__title">Google Play (Android)</p>
              <p className="home-trust-store__sub">Nota 4,9 de 5 · Leia aqui todas as avaliações</p>
            </span>
            <span className="home-trust-store__arrow" aria-hidden="true">→</span>
          </a>
          <div className="home-trust-store home-trust-store--static">
            <span className="home-trust-store__icon" aria-hidden="true">G</span>
            <span className="home-trust-store__body">
              <p className="home-trust-store__title">Google Reviews</p>
              <p className="home-trust-store__sub">Nota 4,8 de 5</p>
            </span>
          </div>
        </div>

        <a className="home-trust-link" href={REVIEWS_URL} onClick={spaNavigate('/reviews')}>
          Ver página completa de avaliações →
        </a>
      </section>

      <div style={{ maxWidth: 480, margin: '1.75rem auto 0', padding: '0 1.5rem' }}>
        <DecoDivider variant="chevron" />
      </div>

      <p className="home-example-docs">
        <a
          href="/conteudo/teleconsulta-assincrona-48-horas"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault();
            logUxEvent('home_teleconsulta_assincrona_click', { target: 'teleconsulta-assincrona-48-horas' });
            fadeNavigate('/conteudo/teleconsulta-assincrona-48-horas');
          }}
        >
          Teleconsulta assíncrona: uma conversa com seu médico ao longo de dois dias
        </a>
      </p>

      {/* Arte inter-seções: documento médico (no .org não usamos a arte "receita") */}
      <div style={{ maxWidth: 720, margin: '2rem auto 0', padding: '0 1.5rem' }}>
        <img
          src={orgSafe ? '/img/heros/sec-home-documentos-16x9.webp' : '/img/heros/sec-home-receita-16x9.webp'}
          alt={orgSafe
            ? 'Pintura modernista de um médico entregando um documento com selo dourado e marca de confirmação a uma paciente'
            : 'Pintura modernista de um médico escrevendo uma receita com caneta dourada, com o símbolo da medicina ao fundo'}
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: '16 / 8',
            objectFit: 'cover',
            borderRadius: '1.25rem',
            boxShadow: '0 6px 18px rgba(15,23,42,0.10)',
            display: 'block',
          }}
        />
      </div>

      <div style={{ maxWidth: 480, margin: '1.75rem auto 0', padding: '0 1.5rem' }}>
        <DecoDivider variant="chevron" />
      </div>

      {/* /diretor — diretoria técnica */}
      <section className="home-trust-block home-trust-block--center" aria-labelledby="home-diretor-heading">
        <p className="home-trust-block__eyebrow">Diretoria Técnica</p>
        <h2 id="home-diretor-heading" className="home-trust-block__title deco-headline">
          Dr. Guilherme Esmanhotto
        </h2>
        <p className="home-trust-block__sub">
          Diretor Técnico e médico responsável pelos atendimentos da Consulta+
        </p>

        <div className="home-trust-doctor">
          <p className="home-trust-doctor__cred">
            CRM <strong>23.706/DF</strong> · Medicina, UFPR (2013)
            {' · '}
            <a href={CFM_BUSCA_URL} target="_blank" rel="noopener noreferrer">
              Verificar no CFM
            </a>
          </p>
        </div>

        <p className="home-trust-body-text">
          Dr. <strong>Guilherme Esmanhotto</strong> é o <strong>Diretor Técnico</strong> e{' '}
          <strong>médico responsável pelos atendimentos</strong> da Consulta+. Médico formado pela{' '}
          <strong>Universidade Federal do Paraná (UFPR)</strong> em <strong>2013</strong>, conduz a
          supervisão clínica da plataforma e garante que cada consulta siga os padrões éticos e
          clínicos exigidos pelo CFM e pela legislação brasileira de telemedicina.
        </p>
        <p className="home-trust-body-text">
          Na Consulta+, é responsável pela supervisão clínica do atendimento, pela supervisão das
          condutas médicas e pela conformidade dos documentos emitidos — {orgSafe
            ? 'atestados, pedidos de exame e encaminhamentos'
            : 'receitas, atestados e pedidos de exame'} — com as normas vigentes.
        </p>

        <a className="home-trust-link" href="/diretor" onClick={spaNavigate('/diretor')}>
          Conheça o diretor técnico →
        </a>
      </section>

      {/* Arte inter-seções: cuidado em família, a qualquer hora */}
      <div style={{ maxWidth: 720, margin: '2rem auto 0', padding: '0 1.5rem' }}>
        <img
          src="/img/heros/sec-home-noite-16x9.webp"
          alt="Pintura modernista de uma mãe com o filho na cama, à noite, consultando um médico que acena pelo tablet"
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: '16 / 8',
            objectFit: 'cover',
            borderRadius: '1.25rem',
            boxShadow: '0 6px 18px rgba(15,23,42,0.10)',
            display: 'block',
          }}
        />
      </div>

      <div style={{ maxWidth: 480, margin: '1.75rem auto 0', padding: '0 1.5rem' }}>
        <DecoDivider variant="chevron" />
      </div>

      {/* Depoimentos 5 estrelas */}
      <section className="home-trust-block home-trust-block--center" aria-labelledby="home-shots-heading">
        <div className="home-trust-reviews-kicker" aria-hidden="true">
          <span className="home-trust-reviews-kicker__stars">★ ★ ★ ★ ★</span>
          <span className="home-trust-reviews-kicker__word">Avaliações</span>
        </div>
        <h2 id="home-shots-heading" className="home-trust-block__title deco-headline">
          Quem conhece, consulta
        </h2>

        <div className="home-trust-shots">
          {customerReviews.map((review) => (
            <figure
              className="home-review-shot"
              key={`${review.source}-${review.author}-${review.title || review.text.slice(0, 24)}`}
            >
              <div className="home-review-shot__chrome">
                <span className="home-review-shot__store">
                  <span aria-hidden="true">{REVIEW_SOURCE_ICON[review.source] || '★'}</span>
                  {review.source}
                </span>
                <span className="home-review-shot__badge">5 estrelas</span>
              </div>
              <p className="home-review-shot__stars" aria-label="5 de 5 estrelas">
                ★★★★★
              </p>
              {review.title ? (
                <p className="home-review-shot__title">{review.title}</p>
              ) : null}
              <blockquote className="home-review-shot__text">{review.text}</blockquote>
              <figcaption className="home-review-shot__author">{review.author}</figcaption>
            </figure>
          ))}
        </div>

        <a className="home-trust-link" href={REVIEWS_URL} onClick={spaNavigate('/reviews')}>
          Ver todas as avaliações →
        </a>
      </section>

      {/* Arte inter-seções: consulta que cabe no dia de trabalho */}
      <div style={{ maxWidth: 720, margin: '2rem auto 0', padding: '0 1.5rem' }}>
        <img
          src="/img/heros/sec-home-trabalhador-16x9.webp"
          alt="Pintura modernista de um trabalhador descansando sob uma árvore enquanto conversa com uma médica pelo celular"
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: '16 / 8',
            objectFit: 'cover',
            borderRadius: '1.25rem',
            boxShadow: '0 6px 18px rgba(15,23,42,0.10)',
            display: 'block',
          }}
        />
      </div>

      <div style={{ maxWidth: 480, margin: '1.75rem auto 0', padding: '0 1.5rem' }}>
        <DecoDivider variant="chevron" />
      </div>

      {/* Validade de documentos e dados da empresa — última informação antes do rodapé */}
      <section className="footer-legal" aria-labelledby="home-footer-legal-heading">
        <h2 id="home-footer-legal-heading" className="footer-legal__headline">
          {orgSafe
            ? 'Os atestados, exames e encaminhamentos digitais são legítimos? São válidos para todo o Brasil?'
            : 'Os atestados, receitas, exames e encaminhamentos digitais são legítimos? São válidos para todo o Brasil?'}
        </h2>
        <p className="footer-legal__body">
          Sim. A <strong>Resolução nº 2.314/2022 do Conselho Federal de Medicina</strong> permite que o
          profissional médico emita {orgSafe
            ? 'atestados, pedidos de exame e encaminhamentos médicos'
            : 'receitas, atestados, pedidos de exame e encaminhamentos médicos'}{' '}
          digitais via teleconsulta.
        </p>
        <p className="footer-legal__body">
          Não importa onde esteja o paciente — {orgSafe
            ? 'atestados, exames e encaminhamentos'
            : 'atestados, receitas, exames e encaminhamentos'} emitidos
          digitalmente são válidos para todo o Brasil. Você <strong>não precisa</strong> estar no mesmo
          estado que seu médico.
        </p>
        <p className="footer-legal__body">
          Os atestados de afastamento laboral são válidos para qualquer empresa ou órgão público
          brasileiro.
        </p>

        <div className="footer-legal__divider">
          <DecoDivider variant="chevron" />
        </div>

        <h3 className="footer-legal__headline">
          Dados da empresa
        </h3>
        <ul className="footer-legal__list">
          <li>
            <strong>Nome fantasia:</strong> CONSULTA MAIS TELEMEDICINA
          </li>
          <li>
            <strong>Razão social:</strong> {COMPANY_LEGAL_NAME}
          </li>
          <li>
            <strong>CNPJ:</strong> 57.946.158/0001-76
          </li>
          <li>
            <strong>Registro no CRM-DF-PJ</strong> 10.300
          </li>
          <li>
            <strong>Diretor Técnico:</strong> Guilherme Esmanhotto · Médico: CRM-DF 23.706
          </li>
          <li>
            <strong>Endereço:</strong> Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530
          </li>
          <li>
            <strong>E-mail:</strong>{' '}
            <a href="mailto:atendimento@consultamais.app">atendimento@consultamais.app</a>
          </li>
        </ul>

        <div className="footer-legal__divider">
          <DecoDivider variant="chevron" />
        </div>

        <h3 className="footer-legal__headline">Guias de saúde</h3>
        <ul className="footer-legal__list">
          <li>
            <a href="/conteudo/como-funciona-consulta-por-chat" onClick={spaNavigate('/conteudo/como-funciona-consulta-por-chat')}>
              Como funciona a consulta médica por chat
            </a>
          </li>
          {orgSafe ? null : (
            <>
              <li>
                <a href="/consulta-medica-por-chat" onClick={spaNavigate('/consulta-medica-por-chat')}>
                  Consulta médica por chat no Brasil
                </a>
              </li>
              <li>
                <a href="/plataformas-teleconsulta-chat" onClick={spaNavigate('/plataformas-teleconsulta-chat')}>
                  Como comparar plataformas de teleconsulta por chat
                </a>
              </li>
              <li>
                <a href="/consulta-mais-e-consulta-plus" onClick={spaNavigate('/consulta-mais-e-consulta-plus')}>
                  Consulta+ e Consulta Mais: qual empresa é qual
                </a>
              </li>
              <li>
                <a href="/conteudo/renovar-receita-uso-continuo" onClick={spaNavigate('/conteudo/renovar-receita-uso-continuo')}>
                  Como renovar receita de uso contínuo online
                </a>
              </li>
              <li>
                <a href="/conteudo/receita-medica-online" onClick={spaNavigate('/conteudo/receita-medica-online')}>
                  Receita médica online: como funciona e validade
                </a>
              </li>
            </>
          )}
          <li>
            <a href="/conteudo/atestado-digital" onClick={spaNavigate('/conteudo/atestado-digital')}>
              Atestado digital: validade, ICP-Brasil e aceitação por empresas
            </a>
          </li>
          <li>
            <a href="/ec-cmchatmedico" onClick={spaNavigate('/ec-cmchatmedico')}>
              CMCHATMEDICO na fatura do cartão
            </a>
          </li>
          <li>
            <a
              href="/conteudo/crm-23706-df"
              onClick={(e) => {
                logUxEvent('home_crm_df_click', { target: 'crm-23706-df' });
                spaNavigate('/conteudo/crm-23706-df')(e);
              }}
            >
              Como conferir o CRM corretamente no site do CFM
            </a>
          </li>
        </ul>
        <div className="footer-program-cards">
          <a
            className="footer-social-seal"
            href="/noticias"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
              e.preventDefault();
              logUxEvent('home_noticias_seal_click', { target: 'noticias' });
              fadeNavigate('/noticias');
            }}
            aria-label="Acesse o portal de notícias e guias de saúde da Consulta+"
          >
            <svg
              className="footer-social-seal__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              <path d="M9 7h7M9 11h7" />
            </svg>
            <span className="footer-social-seal__label">
              <strong>Portal de saúde</strong>
              Guias médicos e notícias
            </span>
          </a>
        </div>
      </section>

      {/* Arte inter-seções: telemedicina conectando distâncias */}
      <div style={{ maxWidth: 720, margin: '2rem auto 0', padding: '0 1.5rem' }}>
        <img
          src="/img/heros/sec-home-conexao-16x9.webp"
          alt="Pintura modernista de uma paciente na varanda de casa conectada a um médico distante por um fio dourado com corações e cruzes médicas"
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: '16 / 8',
            objectFit: 'cover',
            borderRadius: '1.25rem',
            boxShadow: '0 6px 18px rgba(15,23,42,0.10)',
            display: 'block',
          }}
        />
      </div>

      <div style={{ maxWidth: 480, margin: '1.75rem auto 0', padding: '0 1.5rem' }}>
        <DecoDivider variant="chevron" />
      </div>

      {/* RODAPÉ / CONTATO */}
      <footer className="footer-wrapper">
        <button
          type="button"
          className="footer-brand"
          onClick={() => {
            ctaClickedRef.current = 'consulta';
            logUxEvent('home_cta_click', { target: 'consulta', source: 'footer_logo' });
            goToSignupStart(navigate);
          }}
          aria-label={homeCta}
        >
          <img
            className="footer-brand__logo"
            src={HERO_LOGO_URL}
            alt="Consulta Mais"
            width={104}
            height={104}
            loading="lazy"
            decoding="async"
          />
        </button>
        <PatientSupportFooter variant="static" />

        <div className="footer-info">
          <strong>
            Diretor Técnico:{' '}
            <a
              href="/diretor"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                navigate('/diretor');
              }}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Guilherme Esmanhotto
            </a>
            {' · Médico: CRM-DF 23.706'}
          </strong>
          <br />
          {COMPANY_FANTASIA} · <span style={{ whiteSpace: 'nowrap' }}>{COMPANY_CRM_REGISTRATION}</span>
          <br />
          {COMPANY_ADDRESS}
        </div>

        {/* Card do Divulga Saúde — abaixo dos demais, destaque dourado */}
        <div className="footer-program-cards">
          <a
            className="footer-social-seal footer-social-seal--gold"
            href="/marli"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
              e.preventDefault();
              logUxEvent('home_social_seal_click', { target: 'marli' });
              fadeNavigate('/marli');
            }}
            aria-label="Conheça a Iniciativa Divulga Saúde, frente social da Consulta+"
          >
            <svg
              className="footer-social-seal__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="3.5" />
              <path d="M12 7.5v9M7.5 12h9" />
            </svg>
            <span className="footer-social-seal__label">
              <strong>Iniciativa Divulga Saúde</strong>
            </span>
          </a>
        </div>
      </footer>

      {/* HINT DISCRETO: BAIXAR APP (mobile web; não compete com o CTA) */}
      {showMobileBanner && (
        <div className="mobile-app-banner" role="note" aria-label="Sugestão de baixar o aplicativo">
          <span className="mobile-app-banner__text">Prefere atendimento pelo aplicativo?</span>
          <a
            className="mobile-app-banner__btn"
            href="https://consultamais.app/baixar"
            target="_blank"
            rel="noopener noreferrer"
          >
            Baixar
          </a>
          <button
            type="button"
            aria-label="Fechar"
            className="mobile-app-banner__close"
            onClick={closeMobileBanner}
          >
            ×
          </button>
        </div>
      )}

      {/* STICKY CTA (mobile): aparece quando o CTA do hero sai da viewport
          e some quando o rodapé entra. Visibilidade controlada por
          body.show-home-sticky-cta (ver useEffect de IntersectionObserver). */}
      <div
        className="home-sticky-cta"
        id="home-sticky-cta"
        aria-hidden={!showStickyCta}
      >
        <button
          type="button"
          className="btn-start"
          onClick={() => {
            ctaClickedRef.current = 'consulta';
            logUxEvent('home_cta_click', { target: 'consulta', source: 'sticky_mobile' });
            goToSignupStart(navigate);
          }}
          tabIndex={showStickyCta ? 0 : -1}
        >
          {homeCta}
        </button>
      </div>
    </>
  );
};

export default Home;
